/**
 * (Shared: the extension and the Discord bot both use this.)
 *
 * Flight predictions. Torn says when someone is "Traveling to Japan" but not when
 * they'll land; with when they were first seen flying and the flight time for each
 * way of travelling, Omni-Torn can say when they'll land (or a window, when the
 * take-off wasn't seen exactly).
 *
 * One-way flight times in minutes, standard ticket. Faster ways: airstrip 30%
 * faster, WLT business 50%, business class 70% (multipliers below). Check against
 * Torn's Travel Agency if Torn changes them.
 */

export const DESTINATIONS: Record<string, number> = {
  Mexico: 26,
  'Cayman Islands': 35,
  Canada: 41,
  Hawaii: 134,
  'United Kingdom': 159,
  Argentina: 167,
  Switzerland: 175,
  Japan: 225,
  China: 242,
  UAE: 271,
  'South Africa': 297,
};

export const TRAVEL_TYPES = { standard: 1, airstrip: 0.7, wlt: 0.5, business: 0.3 } as const;
export type TravelType = keyof typeof TRAVEL_TYPES;

/** A destination's name as DESTINATIONS spells it ("uk" → United Kingdom), or undefined. */
const place = (name: string) => Object.keys(DESTINATIONS).find((k) => k.toLowerCase() === name.trim().toLowerCase() || (k === 'United Kingdom' && /^uk$/i.test(name.trim())) || (k === 'UAE' && /emirates/i.test(name)));
/** A destination's name as DESTINATIONS spells it ("uk" → "United Kingdom"), or undefined (the flight tracker, flights.ts). */
export const destinationName = (name: string): string | undefined => place(name);

/**
 * "Traveling to Japan" / "Returning to Torn from Japan" / "In Japan" — and Torn's 2026 wording, "Traveling from Torn to
 * Japan" / "Traveling from Japan to Torn" (suggestion 024612-bb7d: those read as just "flying") → where and which way.
 */
export function readTravel(description: string): { destination: string; leg: 'out' | 'back' | 'there' } | null {
  const d = description.trim();
  let m = /^Traveling from (.+?) to (.+?)\.?$/i.exec(d);
  if (m) {
    const [from, to] = [m[1]!.trim(), m[2]!.trim()];
    if (/^torn$/i.test(to) && place(from)) return { destination: place(from)!, leg: 'back' };
    if (/^torn$/i.test(from) && place(to)) return { destination: place(to)!, leg: 'out' };
    return null;
  }
  m = /^Traveling to (.+?)\.?$/i.exec(d);
  if (m) return place(m[1]!) ? { destination: place(m[1]!)!, leg: 'out' } : null;
  m = /^Returning to Torn from (.+?)\.?$/i.exec(d);
  if (m) return place(m[1]!) ? { destination: place(m[1]!)!, leg: 'back' } : null;
  m = /^In (.+?)\.?$/i.exec(d);
  if (m) return place(m[1]!) ? { destination: place(m[1]!)!, leg: 'there' } : null;
  return null;
}

/**
 * Torn's blue flying bar on a profile (2026): it shows "South Africa to Torn", with the aria-label "Traveling from South
 * Africa to Torn . Type of plane: Personal" and classes like "travelling from south africa airstrip" — or the older
 * "Traveling to Japan". Where to, which way, the plane when Torn names it, and the API's wording ("Traveling to X" /
 * "Returning to Torn from X") for the rest of the code (bug 174817-927f, suggestion 180400-c72e).
 */
export function readProfileFlight(text: string, label = '', cls = ''): { destination: string; leg: 'out' | 'back'; plane: TravelType | null; description: string } | null {
  let destination: string | undefined;
  let leg: 'out' | 'back' | null = null;
  const old = readTravel(text) ?? readTravel(label);
  if (old && old.leg !== 'there') {
    destination = old.destination;
    leg = old.leg;
  } else {
    const m = /traveling from (.+?) to (.+?)\s*(?:\.|$)/i.exec(label) ?? /^\s*(.+?) to (.+?)\s*\.?\s*$/i.exec(text);
    if (m) {
      const [from, to] = [m[1]!.trim(), m[2]!.trim()];
      if (/^torn$/i.test(to) && place(from)) [destination, leg] = [place(from), 'back'];
      else if (/^torn$/i.test(from) && place(to)) [destination, leg] = [place(to), 'out'];
    }
  }
  if (!destination || !leg) return null;
  return { destination, leg, plane: planeType(`${/type of plane:\s*([a-z ]+)/i.exec(label)?.[1] ?? ''} ${cls}`), description: leg === 'out' ? `Traveling to ${destination}` : `Returning to Torn from ${destination}` };
}

/** The plane from Torn's words for it ("Personal" / class "airstrip" = a Private Island's airstrip), or null. */
function planeType(words: string): TravelType | null {
  if (/\b(airstrip|personal)\b/i.test(words)) return 'airstrip';
  if (/\bbusiness\b/i.test(words)) return 'business';
  if (/\b(private|jet)\b/i.test(words)) return 'wlt';
  if (/\b(airliner|commercial|standard|economy)\b/i.test(words)) return 'standard';
  return null;
}

export interface Landing {
  type: TravelType;
  /** Earliest and latest landing (ms), given when take-off could have been. */
  earliest: number;
  latest: number;
}

/**
 * When they land, for each way of travelling.
 * `tookOffAfter`: the last time they were seen not flying (or null if unknown);
 * `seenFlyingAt`: the first time they were seen flying.
 */
export function predictLanding(destination: string, tookOffAfter: number | null, seenFlyingAt: number, now: number = Date.now()): Landing[] {
  const minutes = DESTINATIONS[destination];
  if (!minutes) return [];
  return (Object.keys(TRAVEL_TYPES) as TravelType[])
    .map((type) => {
      const flight = minutes * TRAVEL_TYPES[type] * 60_000;
      const earliest = (tookOffAfter ?? seenFlyingAt - flight) + flight;
      const latest = seenFlyingAt + flight;
      return { type, earliest: Math.max(earliest, Math.min(latest, now)), latest };
    })
    .filter((l) => l.latest >= now);
}

/**
 * From a player's status history (newest first): the flight they're on, if any —
 * where to, when they were first seen flying, and the last time before that they
 * were seen not flying.
 */
export function currentFlight(history: ReadonlyArray<{ at: number; state: string; description: string }>): { destination: string; leg: 'out' | 'back'; seenFlyingAt: number; tookOffAfter: number | null } | null {
  const now = history[0];
  if (!now) return null;
  const t = readTravel(now.description);
  if (!t || t.leg === 'there') return null;
  let first = now;
  let i = 1;
  while (i < history.length && history[i]!.description === now.description) first = history[i++]!;
  return { destination: t.destination, leg: t.leg, seenFlyingAt: first.at, tookOffAfter: history[i]?.at ?? null };
}
